import type { CSSProperties, SVGProps } from "react";

/**
 * Inline SVG icons (Lucide style: 24x24 viewBox, 2px stroke, round caps).
 * Subset of Lucide — only the icons the auth flows reference. Inlined
 * to avoid a runtime dependency for ~10 glyphs.
 */
export type IconName =
  | "arrow-left"
  | "arrow-right"
  | "bell"
  | "check"
  | "eye"
  | "eye-off"
  | "info"
  | "lock"
  | "mail"
  | "minus"
  | "refresh"
  | "send"
  | "user"
  | "zap"
  | "image"
  | "copy"
  | "edit"
  | "calendar"
  | "x"
  | "alert"
  | "x-circle"
  | "check-circle"
  | "play"
  | "arrow-up-down"
  | "slack"
  | "store"
  | "arrow-up-right"
  | "sparkles"
  | "smartphone"
  | "home"
  | "link";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "size"> {
  name: IconName;
  size?: number | string;
  color?: string;
  strokeWidth?: number;
  style?: CSSProperties;
}

// Each icon is one or more SVG paths joined by "|". The renderer splits
// and emits a <path> per segment.
const PATHS: Record<IconName, string> = {
  "arrow-left": "M19 12H5|M12 19l-7-7 7-7",
  "arrow-right": "M5 12h14|M12 5l7 7-7 7",
  bell: "M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9|M13.73 21a2 2 0 0 1-3.46 0",
  check: "M20 6 9 17l-5-5",
  eye: "M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z|M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z",
  "eye-off":
    "m3 3 18 18|M10.58 10.58a3 3 0 0 0 4.24 4.24|M9.88 4.24A9.46 9.46 0 0 1 12 4c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68|M6.61 6.61A13.53 13.53 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61",
  info: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z|M12 16v-4|M12 8h.01",
  lock: "M5 11h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z|M7 11V7a5 5 0 0 1 10 0v4",
  mail: "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z|m22 6-10 7L2 6",
  minus: "M5 12h14",
  refresh:
    "M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.7-3|M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.7 3|M21 3v6h-6|M3 21v-6h6",
  send: "m22 2-7 20-4-9-9-4 20-7Z|M22 2 11 13",
  user: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2|M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
  zap: "M13 2 3 14h9l-1 8 10-12h-9l1-8Z",
  image:
    "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z|M8.5 8.5h.01|m3 16 5-5 4 4|m14 13 2-2 5 5",
  copy:
    "M9 9h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2Z|M5 15a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2",
  edit:
    "M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7|M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z",
  calendar:
    "M8 2v4|M16 2v4|M3 10h18|M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z",
  x: "M18 6 6 18|M6 6l12 12",
  alert:
    "M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z|M12 9v4|M12 17h.01",
  "x-circle": "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z|m15 9-6 6|m9 9 6 6",
  "check-circle": "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z|m9 12 2 2 4-4",
  play: "M6 4v16l14-8-14-8Z",
  "arrow-up-down": "m21 16-4 4-4-4|M17 20V4|m3 8 4-4 4 4|M7 4v16",
  slack:
    "M14.5 10c-.83 0-1.5-.67-1.5-1.5v-5c0-.83.67-1.5 1.5-1.5S16 2.67 16 3.5V8.5c0 .83-.67 1.5-1.5 1.5Z|M20.5 10H19V8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5Z|M9.5 14c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5S8 21.33 8 20.5V15.5c0-.83.67-1.5 1.5-1.5Z|M3.5 14H5v1.5c0 .83-.67 1.5-1.5 1.5S2 16.33 2 15.5 2.67 14 3.5 14Z|M14 14.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5H15.5c-.83 0-1.5-.67-1.5-1.5Z|M14 20.5V19h1.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5Z|M10 9.5c0 .83-.67 1.5-1.5 1.5h-5C2.67 11 2 10.33 2 9.5S2.67 8 3.5 8H8.5c.83 0 1.5.67 1.5 1.5Z|M10 3.5V5H8.5C7.67 5 7 4.33 7 3.5S7.67 2 8.5 2 10 2.67 10 3.5Z",
  store:
    "M3 9 4 4h16l1 5|M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9|M3 9h18|M9 21v-6h6v6",
  "arrow-up-right": "M7 17 17 7|M7 7h10v10",
  sparkles:
    "M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z|M19 14l.8 2L22 16.8l-2.2.8L19 20l-.8-2.4L16 16.8l2.2-.8L19 14Z",
  smartphone:
    "M7 2h10a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z|M11 19h2",
  home: "m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z|M9 22V12h6v10",
  link:
    "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71|M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
};

// Icons that are filled shapes (not stroked outlines), e.g. the play
// triangle. Rendered with fill=color + stroke=none.
const FILLED = new Set<IconName>(["play"]);

export function Icon({
  name,
  size = 16,
  color = "currentColor",
  strokeWidth = 2,
  style,
  ...rest
}: IconProps) {
  const filled = FILLED.has(name);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? color : "none"}
      stroke={filled ? "none" : color}
      strokeWidth={filled ? 0 : strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
      {...rest}
    >
      {PATHS[name].split("|").map((seg, i) => (
        <path key={i} d={seg} />
      ))}
    </svg>
  );
}
